import React from 'react'
import { useSelector } from 'react-redux'
import {
    Card,
    ErrorState,
    FormItem,
    FormItemTypes,
    Icon,
    Label,
    ThemeVariants,
    Typography,
} from 'rds'
import { SALE_PAYMENT_TYPES } from '../../constants'
import CheckoutFormSection from './CheckoutFormSection'

const PAYMENT_METHOD_ICON_SIZE = 25

const getPaymentMethodOptions = (
    onlinePaymentMethods = [],
    onsitePaymentMethods = [],
) => {
    const paymentMethodOptions = []
    if (onlinePaymentMethods.includes('MERCADOPAGO')) {
        paymentMethodOptions.push({
            label: 'MercadoPago',
            value: '',
            id: '',
            description: (
                <img
                    className='rds-m_top__sm'
                    alt='MercadoPago'
                    src='/img/mercadopago-logo.png'
                    height={PAYMENT_METHOD_ICON_SIZE}
                    width={PAYMENT_METHOD_ICON_SIZE}
                    style={{ objectFit: 'contain', objectPosition: 'left' }}
                />
            ),
        })
    }
    if (onsitePaymentMethods.includes('CASH')) {
        paymentMethodOptions.push({
            label: 'Efectivo',
            value: 'CASH',
            id: 'CASH',
            description: (
                <div className='rds-m_top__sm'>
                    <Icon
                        type='PaperMoneyTwo'
                        size={PAYMENT_METHOD_ICON_SIZE}
                    />
                </div>
            ),
        })
    }
    const acceptsVISA = onsitePaymentMethods.includes('VISA')
    const acceptsMASTER = onsitePaymentMethods.includes('MASTERCARD')
    if (acceptsVISA || acceptsMASTER) {
        paymentMethodOptions.push({
            label: 'POS',
            value: 'POS',
            id: 'POS',
            description: (
                <div className='rds-m_top__sm rds-flex rds-align-center'>
                    {acceptsVISA ? (
                        <img
                            className='rds-m_right__sm'
                            src='/img/visa.svg'
                            alt='VISA'
                            height={PAYMENT_METHOD_ICON_SIZE}
                            width={PAYMENT_METHOD_ICON_SIZE}
                            style={{
                                objectFit: 'contain',
                                objectPosition: 'left',
                            }}
                        />
                    ) : null}
                    {acceptsMASTER ? (
                        <img
                            className='rds-m_right__sm'
                            src='/img/master-card.svg'
                            alt='MasterCard'
                            height={PAYMENT_METHOD_ICON_SIZE}
                            width={PAYMENT_METHOD_ICON_SIZE}
                            style={{
                                objectFit: 'contain',
                                objectPosition: 'left',
                            }}
                        />
                    ) : null}
                </div>
            ),
        })
    }
    const acceptsMercadoPago = onsitePaymentMethods.includes('MERCADOPAGO')
    if (acceptsMercadoPago) {
        paymentMethodOptions.push({
            label: 'MercadoPago (QR)',
            value: 'MERCADOPAGO',
            id: 'MERCADOPAGO',
            description: (
                <img
                    className='rds-m_top__sm'
                    alt='MercadoPago (QR)'
                    src='/img/mercadopago-logo.png'
                    height={PAYMENT_METHOD_ICON_SIZE}
                    width={PAYMENT_METHOD_ICON_SIZE}
                    style={{ objectFit: 'contain', objectPosition: 'left' }}
                />
            ),
        })
    }
    const acceptsMoneyTransfer = onsitePaymentMethods.includes('MONEY_TRANSFER')
    if (acceptsMoneyTransfer) {
        paymentMethodOptions.push({
            label: 'Transferencia',
            value: 'MONEY_TRANSFER',
            id: 'MONEY_TRANSFER',
            description: (
                <div className='rds-m_top__sm'>
                    <Icon type='Exchange' size={PAYMENT_METHOD_ICON_SIZE} />
                </div>
            ),
        })
    }
    return paymentMethodOptions
}

const CheckoutFormPaymentMethod = ({ checkout, onChange }) => {
    const locations = useSelector(state => state.location.locations)
    const selectedLocationId = useSelector(
        state => state.location.selectedLocationId,
    )
    const selectedLocation = locations.find(
        location => location.id === selectedLocationId,
    )
    const selectedLocationPaymentMethods =
        selectedLocation?.paymentMethods || {}
    const onlinePaymentMethods = selectedLocationPaymentMethods.online || []
    const onsitePaymentMethods = selectedLocationPaymentMethods.onsite || []
    const paymentMethodOptions = getPaymentMethodOptions(
        onlinePaymentMethods,
        onsitePaymentMethods,
    )

    const changePaymentMethod = React.useCallback(
        paymentMethod => {
            onChange(
                'paymentType',
                paymentMethod
                    ? SALE_PAYMENT_TYPES.ONSITE
                    : SALE_PAYMENT_TYPES.ONLINE,
            )
            onChange('onSitePayment.method', paymentMethod)
            onChange(
                'onSitePayment.payingWithCashAmount',
                paymentMethod === 'CASH'
                    ? checkout.onSitePayment?.payingWithCashAmount
                    : '',
            )
        },
        [onChange, checkout.onSitePayment?.payingWithCashAmount],
    )

    React.useEffect(() => {
        if (!selectedLocationId) {
            return
        }
        // Clear payment method if no options are available
        if (!paymentMethodOptions.length && checkout.onSitePayment?.method) {
            changePaymentMethod()
            return
        }
        // Default payment method if the previously selected one is no longer available
        if (
            paymentMethodOptions.length &&
            !paymentMethodOptions.find(
                option => option.value === checkout.onSitePayment?.method,
            )
        ) {
            const firstOptionValue = paymentMethodOptions[0]?.value || ''
            changePaymentMethod(firstOptionValue)
        }
    }, [
        selectedLocationId,
        paymentMethodOptions,
        checkout.onSitePayment?.method,
        changePaymentMethod,
    ])

    const handlePaymentMethodChange = event => {
        const { value } = event.target
        changePaymentMethod(value)
    }

    return (
        <CheckoutFormSection title='Método de pago'>
            {paymentMethodOptions.length ? (
                <FormItem
                    type={FormItemTypes.CARD_SELECT}
                    values={paymentMethodOptions}
                    value={checkout.onSitePayment.method}
                    onChange={handlePaymentMethodChange}
                    required
                />
            ) : (
                <Card
                    variant={ThemeVariants.WARNING}
                    className='rds-grid_2-columns_item'
                >
                    <ErrorState
                        title='No es posible obtener los métodos de pago'
                        message='Intenta nuevamente en unos segundos'
                    />
                </Card>
            )}
            {checkout.onSitePayment.method === 'CASH' ? (
                <FormItem
                    type={FormItemTypes.NUMBER}
                    label='Monto'
                    placeholder='Monto en efectivo con el que abonarás'
                    tooltip='Este dato nos ayuda a poder enviar el dinero necesario para el cambio'
                    value={checkout.onSitePayment.payingWithCashAmount}
                    onChange={event => {
                        onChange(
                            'onSitePayment.payingWithCashAmount',
                            event.target.value,
                        )
                    }}
                    min={1}
                />
            ) : null}
            {checkout.onSitePayment.method === 'MONEY_TRANSFER' ? (
                <div className='rds-m_top__md'>
                    <Label>Indicaciones para transferencia</Label>
                    <Typography>
                        {
                            selectedLocation.paymentMethods
                                .moneyTransferDescription
                        }
                    </Typography>
                </div>
            ) : null}
        </CheckoutFormSection>
    )
}

export default CheckoutFormPaymentMethod
